/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';

import { ButtonSetButton } from '../UNSAFE_ButtonSetButton';
import { ButtonSetIconButton } from '../UNSAFE_ButtonSetIconButton';
import { useCallback } from 'preact/hooks';

import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useButtonSetContext } from '../hooks/UNSAFE_useButtonSetContext';
import { useButtonSetPositionContext } from '../hooks/UNSAFE_useButtonSetPositionContext';
import { toggleButtonSetItem } from '#utils/UNSAFE_buttonUtils';

export type ButtonSetItemProps = TestIdProps & {
  /**
   * Value of the selected buttonSetItem option.
   */
  value: string;

  /**
   * label - label
   */
  label?: string;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Specifies that the ButtonSetSingle element should be disabled.
   */
  isDisabled?: boolean;
};

/**
 * A ButtonSetItem specifies buttons for a buttonset.
 */
export const ButtonSetItem = ({ isDisabled: isPropsDisabled, ...props }: ButtonSetItemProps) => {
  // The buttonSetItem context determines if a buttonset button or icon button rendered
  // The buttonset context control provides variant, size, isItemDisabled, inputType, inputName. value
  // The buttonset position control provides position, isSelected,
  const {
    display,
    inputType,
    isDisabled: isSetDisabled,
    buttonSetValue,
    onCommit,
    ...buttonsetProps
  } = useButtonSetContext();

  const positionProps = useButtonSetPositionContext();
  // The property isItemDisabled, if defined, overrides buttonSetItem settings.
  const isItemDisabled = isPropsDisabled ?? isSetDisabled;
  const iconProps = {
    tooltip: props.label,
    value: props.value
  };

  const isRadio = inputType === 'radio';

  const onToggleHandlerSingle = useCallback(() => {
    if (isRadio) {
      onCommit?.({
        previousValue: buttonSetValue,
        value: [props.value]
      });
    }
  }, [buttonSetValue, onCommit, isRadio, props.value]);

  const onToggleHandlerMultiple = useCallback(() => {
    if (!isRadio) {
      const newButtonSetValue = toggleButtonSetItem(props.value, buttonSetValue);
      onCommit?.({
        previousValue: buttonSetValue,
        value: newButtonSetValue
      });
    }
  }, [buttonSetValue, onCommit, isRadio, props.value]);

  // When toggled, the button invokes the buttonset commit, which can update the buttonset value, which then
  // updates the buttonset context buttonSetValue causing this buttonSetItem to render appropriately.
  return display === 'icons' ? (
    <ButtonSetIconButton
      isSelected={isButtonSetItemSelected(props.value, buttonSetValue)}
      onToggle={isRadio ? onToggleHandlerSingle : onToggleHandlerMultiple}
      isDisabled={isItemDisabled}
      {...iconProps}
      {...positionProps}
      {...buttonsetProps}>
      {props.startIcon}
    </ButtonSetIconButton>
  ) : (
    <ButtonSetButton
      isSelected={isButtonSetItemSelected(props.value, buttonSetValue)}
      onToggle={isRadio ? onToggleHandlerSingle : onToggleHandlerMultiple}
      isDisabled={isItemDisabled}
      {...props}
      {...positionProps}
      {...buttonsetProps}></ButtonSetButton>
  );
};

/**
 * Check if buttonSetValue contains the buttonSetItem.
 * Note the buttonSetValue can be a set of strings or a string, depending on whether a checkbox or
 * radio type is used.
 * @param buttonSetItemValue
 * @param buttonSetValue
 * @returns
 */
function isButtonSetItemSelected(
  buttonSetItemValue: string,
  buttonSetValue: Array<string> | undefined
): boolean {
  if (!buttonSetValue) {
    return false;
  }
  return buttonSetValue?.indexOf(buttonSetItemValue) > -1;
}

ButtonSetItem.displayName = 'ButtonSetItem';
